import { Box, Card, Icon, Tag, Text } from '@zillow/constellation-10';
import { IconChevronRightOutline } from '@zillow/constellation-icons';

import AccessibleCardLink from './AccessibleCardLink';
import { eventCardActionsStyles, eventCardContentStyles, eventCardDetailsStyles } from './styles';
import { getTourStatusTagLabel, getTourStatusTone } from '../../utils/tourStatusDisplay';
import type { UpcomingTourEvent } from '../../hooks/useUpcomingToursData';

type TourEventCardProps = {
  event: UpcomingTourEvent;
  inquiriesPath: string;
  onClick?: () => void;
};

const TourEventCard = ({ event, inquiriesPath, onClick }: TourEventCardProps) => {
  const statusLabel = getTourStatusTagLabel(event.tourStatus);

  // An aria-label replaces the card's content as its accessible name, so the visible status and
  // time have to be folded back in or they are never announced.
  const cardSummary = [event.renterName, statusLabel, event.timeRange].filter(Boolean).join(', ');

  // The chevron is a navigation affordance, so only the linked variant renders it.
  const renderContent = (withChevron: boolean) => (
    <Card.Content>
      <Box css={eventCardContentStyles}>
        <Box css={eventCardDetailsStyles}>
          <Text textStyle="body-bold">{event.renterName}</Text>
          <Text textStyle="body-sm" color="text.subtle">
            {event.timeRange}
          </Text>
        </Box>
        <Box css={eventCardActionsStyles}>
          <Tag tone={getTourStatusTone(event.tourStatus)} size="sm">
            {statusLabel}
          </Tag>
          {withChevron && (
            <Icon
              size="sm"
              css={{ color: 'border.subtle' }}
              render={<IconChevronRightOutline />}
              data-testid={`ToursSummary-eventCardChevron-${event.tourId}`}
            />
          )}
        </Box>
      </Box>
    </Card.Content>
  );

  // A tour without a messaging thread has nowhere to link — a `?conversationId=` href would land on
  // lead management with nothing selected.
  if (!event.conversationId) {
    return (
      <Card.Root elevated={false} data-testid={`ToursSummary-eventCard-${event.tourId}`}>
        {renderContent(false)}
      </Card.Root>
    );
  }

  return (
    <AccessibleCardLink
      href={`${inquiriesPath}?conversationId=${encodeURIComponent(event.conversationId)}`}
      target="_blank"
      rel="noopener noreferrer"
      ariaLabel={`${cardSummary}. View lead details, opens in a new tab`}
      testId={`ToursSummary-eventCard-${event.tourId}`}
      onClick={onClick}
    >
      {renderContent(true)}
    </AccessibleCardLink>
  );
};

export default TourEventCard;
